import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useProject } from '../../contexts/ProjectContext';
import { getArticleDetail, confirmArticlePlan, generateArticlePlan } from '../../services/api';
import { useCoins } from '../../contexts/CoinContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSnackbar } from 'notistack';
import {
  getTypeLabel,
  getSubtypeLabel,
  getPlatformContext
} from '../../constants/articleTypes';

// Step components
import PlanStep from './steps/PlanStep';

const STEPS = [
  {
    id: 'plan',
    label: 'Article Plan',
    component: PlanStep,
    optional: false,
  },
];

const ArticleWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useProject();
  const { updateCoins } = useCoins();
  const { showSubscriptionDialog } = useSubscription();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const loadedRef = useRef(false);
  
  const [loading, setLoading] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [error, setError] = useState(null);
  const [wizardData, setWizardData] = useState({
    article: null,
    plan: '',
  });

  // Get article_id from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const articleId = searchParams.get('article_id');

  useEffect(() => {
    // Load article when component mounts
    if (articleId && !loadedRef.current) {
      loadedRef.current = true;
      loadArticle(articleId);
    }
  }, [articleId]);

  // Load existing article
  const loadArticle = async (id) => {
    try {
      setLoading(true);
      const article = await getArticleDetail(id);
      
      setWizardData({
        article,
        plan: article.raw_plan || ''
      });

      // Only generate plan if article is completely new (no plan and no content)
      if (!article.raw_plan && !article.content) {
        await handleGeneratePlan(id);
      }
    } catch (error) {
      if (error.response?.status === 402) {
        showSubscriptionDialog(error.response.data?.model || '');
      }
      setError(`Error loading article: ${error.response?.data?.detail || error.message}`);
      enqueueSnackbar(`Error loading article: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePlan = async (id, model = null) => {
    // Prevent generating if already in progress
    if (isGeneratingPlan) return;
    
    try {
      setIsGeneratingPlan(true);
      const response = await generateArticlePlan(id, model);
      if (response.plan) {
        setWizardData(prev => ({
          ...prev,
          plan: response.plan
        }));
        if (response.coins_used) {
          updateCoins(-response.coins_used);
        }
        enqueueSnackbar('Plan generated successfully!', { variant: 'success' });
      }
    } catch (error) {
      if (error.response?.status === 402) {
        showSubscriptionDialog(error.response.data?.model || '');
      }
      setError(`Error generating plan: ${error.response?.data?.detail || error.message}`);
      enqueueSnackbar(`Error generating plan: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  // Handle data updates from steps
  const handleStepDataUpdate = (stepId, data) => {
    setWizardData(prev => ({
      ...prev,
      [stepId]: data
    }));
  };

  // Finish wizard and navigate to article
  const handleFinish = async () => {
    try {
      setLoading(true);
      
      // Confirm the plan
      if (wizardData.article && wizardData.plan) {
        await confirmArticlePlan(wizardData.article.id, wizardData.plan);
        
        // Track article plan generation event
        window.gtag('event', 'article_plan_generated', {
          'event_category': 'conversion',
          'event_label': 'article_plan_generated',
          'value': 50,
          'currency': 'EUR',
          'article_type': wizardData.article.type,
          'article_subtype': wizardData.article.subtype || 'general'
        });

        enqueueSnackbar('Article plan confirmed successfully!', { variant: 'success' });
      }

      // Navigate to article details with write_first parameter
      navigate(`/articles/${wizardData.article.id}?write_first=true`);
    } catch (error) {
      setError(`Error: ${error.response?.data?.detail || error.message}`);
      enqueueSnackbar(`Error: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Get current step component
  const CurrentStepComponent = STEPS[0].component;

  if (!articleId) {
    return (
      <Box sx={{ 
        maxWidth: '100%',
        width: '100%',
        mx: 'auto',
        p: { xs: 0, sm: 3 }
      }}>
        <Typography>No article ID provided. Please start from the dashboard.</Typography>
      </Box>
    );
  }

  if (loading && !wizardData.article) {
    return (
      <Box sx={{ 
        maxWidth: '100%',
        width: '100%',
        mx: 'auto',
        p: { xs: 0, sm: 3 },
        textAlign: 'center'
      }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading article...</Typography>
      </Box>
    );
  }

  const isShortPost = wizardData.article?.type === 'short_social_post';

  return (
    <Box sx={{ 
      maxWidth: '100%',
      width: '100%',
      mx: 'auto',
      p: { xs: 0, sm: 3 },
      mt: { xs: -1, sm: 0 },
      bgcolor: 'background.paper',
      position: 'relative'
    }}>
      {(loading || isGeneratingPlan) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1300,
            backdropFilter: 'blur(2px)'
          }}
        >
          <Box sx={{ textAlign: 'center', p: 3, borderRadius: 2 }}>
            <CircularProgress size={50} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {loading ? 'Generating creativity' : 'Crafting your content plan'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {loading ? 'Please wait while we prepare your creative space' : 'Organizing ideas into a perfect structure'}
            </Typography>
          </Box>
        </Box>
      )}
      <Paper sx={{ 
        p: { xs: 1, sm: 3 },
        borderRadius: { xs: 0, sm: 1 },
        boxShadow: { xs: 0, sm: 1 },
        minHeight: { xs: 'calc(100vh - 63px)', sm: 'auto' },
        opacity: loading || isGeneratingPlan ? 0.6 : 1,
        transition: 'opacity 0.2s ease-in-out',
        position: 'relative'
      }}>
        <Typography variant="h5" gutterBottom sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          px: { xs: 1, sm: 0 }
        }}>
          {isShortPost ? 'Creating Short Post' : 'Creating Article'}: {wizardData.article?.title}
        </Typography>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ 
          fontSize: { xs: '0.875rem', sm: '1rem' },
          px: { xs: 1, sm: 0 },
          mb: 1
        }}>
          {getTypeLabel(wizardData.article?.type)}
          {wizardData.article?.subtype && ` • ${getSubtypeLabel(wizardData.article?.subtype)}`}
        </Typography>

        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          px: { xs: 1, sm: 0 },
          mb: 3
        }}>
          <Typography variant="body2" color="text.secondary">
            You're now in the planning phase, where we'll create a structured outline for your content. 
            {isShortPost ? (
              " Since this is a short post, we'll keep it focused with a single section."
            ) : (
              " We'll break down your content into logical sections to ensure a clear flow."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {wizardData.article?.subtype ? (
              <>
                This {getSubtypeLabel(wizardData.article.subtype)} will be optimized for {getPlatformContext(wizardData.article.subtype)}.
              </>
            ) : (
              "Once the plan is ready, you'll be able to start writing your content section by section."
            )}
          </Typography>
        </Box>

        <Box sx={{ 
          display: { xs: 'none', sm: 'flex' },
          justifyContent: 'flex-end',
          mb: 3,
          px: { xs: 1, sm: 0 }
        }}>
          <Button
            variant="contained"
            onClick={handleFinish}
            disabled={loading || !wizardData.plan || isGeneratingPlan}
            sx={{
              position: 'relative',
              minHeight: '36px',
              minWidth: '200px'
            }}
          >
            {(loading || isGeneratingPlan) ? (
              <>
                <CircularProgress 
                  size={20} 
                  sx={{ 
                    color: 'inherit',
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-10px'
                  }} 
                />
                <Box sx={{ opacity: 0 }}>Create Content</Box>
              </>
            ) : (
              'Create Content'
            )}
          </Button>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2, px: { xs: 1, sm: 0 } }}>
            {error}
          </Typography>
        )}

        <Box sx={{ mb: 3, px: { xs: 0, sm: 0 }, pb: { xs: 7, sm: 7 } }}>
          {isShortPost ? (
            <Box sx={{ p: 2 }}>
              
              <CurrentStepComponent
                data={wizardData}
                onDataUpdate={handleStepDataUpdate}
                onGeneratePlan={(model) => handleGeneratePlan(articleId, model)}
                isGenerating={isGeneratingPlan}
              />
            </Box>
          ) : (
            <CurrentStepComponent
              data={wizardData}
              onDataUpdate={handleStepDataUpdate}
              onGeneratePlan={(model) => handleGeneratePlan(articleId, model)}
              isGenerating={isGeneratingPlan}
            />
          )}
        </Box>

        <Box sx={{ 
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: `1px solid ${theme.palette.divider}`,
          p: 2,
          zIndex: 1200,
          maxWidth: { sm: '100%' },
          width: '100%',
          boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.05)'
        }}>
          <Button
            variant="contained"
            onClick={handleFinish}
            disabled={loading || !wizardData.plan || isGeneratingPlan}
            fullWidth={isMobile}
            sx={{
              position: 'relative',
              minHeight: '36px'
            }}
          >
            {(loading || isGeneratingPlan) ? (
              <>
                <CircularProgress 
                  size={20} 
                  sx={{ 
                    color: 'inherit',
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-10px'
                  }} 
                />
                <Box sx={{ opacity: 0 }}>Create Content</Box>
              </>
            ) : (
              'Create Content'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ArticleWizard; 